// Assessor Details View Page - Shows detailed assessor information for an application
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Divider,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  alpha
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Visibility as VisibilityIcon,
  DateRange as DateRangeIcon,
  Assessment as AssessmentIcon,
  Description as DescriptionIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Send as SendIcon,
  AccountBalance as AccountBalanceIcon,
  PictureAsPdf as PictureAsPdfIcon,
  MonetizationOn as MonetizationOnIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon
} from '@mui/icons-material';
import JDApiService from '../../../services/JDApiService';
import authService from '../../../services/authService';
import { decryptText, encryptAppId, encryptText } from '../../../utils/encryption';
import axios from 'axios';
import DoApiService from '../../../services/DoApiService';
import s3Utils from '../../../utils/s3Utils';
import CommonAPIService from '../../../services/commonAPIService';
import { formatDate } from '../../../utils/dateUtils';

// Helper function to determine capacity
const getCapacityLabel = (assessor) => {
  const capacities = [];

  // Check isTA
  if (assessor.isTA === "1" || assessor.isTA === 1 || assessor.isTA === true) {
    capacities.push('TA');
  }

  // Check isLATA
  if (assessor.isLATA === "1" || assessor.isLATA === 1 || assessor.isLATA === true) {
    capacities.push('LA_TA');
  }

  // Check IsLA
  if (assessor.IsLA === "1" || assessor.IsLA === 1 || assessor.IsLA === true) {
    capacities.push('LA');
  }

  // Check Observer
  if (assessor.Observer === "1" || assessor.Observer === 1 || assessor.Observer === true) {
    capacities.push('Observer');
  }

  // Check IsTL (Team Leader)
  if (assessor.IsTL === "1" || assessor.IsTL === 1 || assessor.IsTL === true) {
    capacities.push('TL');
  }

  // Check TE (Technical Expert)
  if (assessor.TE === "1" || assessor.TE === 1 || assessor.TE === true) {
    capacities.push('TE');
  }

  // Check IsTA_DA
  if (assessor.IsTA_DA === "1" || assessor.IsTA_DA === 1) {
    capacities.push('TA/DA');
  }

  // Check IsSCF
  if (assessor.IsSCF === "1" || assessor.IsSCF === 1 || assessor.IsSCF === true) {
    capacities.push('SCF');
  }

  // Remove duplicates and return
  const uniqueCapacities = [...new Set(capacities)];

  // If no capacities found, check the Capacity field directly
  if (uniqueCapacities.length === 0 && assessor.Capacity) {
    return assessor.Capacity.replace(/_/g, ', ');
  }

  return uniqueCapacities.length > 0 ? uniqueCapacities.join(', ') : 'N/A';
};

// Modern color palette
const colors = {
  primary: {
    main: "#6366f1",
    light: "#818cf8",
    dark: "#4f46e5",
    contrast: "#ffffff",
  },
  secondary: {
    main: "#ec4899",
    light: "#f472b6",
    dark: "#db2777",
  },
  success: {
    main: "#10b981",
    light: "#34d399",
    dark: "#059669",
  },
  warning: {
    main: "#f59e0b",
    light: "#fbbf24",
    dark: "#d97706",
  },
  error: {
    main: "#ef4444",
    light: "#f87171",
    dark: "#dc2626",
  },
  grey: {
    50: "#f8fafc",
    100: "#f1f5f9",
    200: "#e2e8f0",
    300: "#cbd5e1",
    400: "#94a3b8",
    500: "#64748b",
    600: "#475569",
    700: "#334155",
    800: "#1e293b",
    900: "#0f172a",
  },
};

const AssessorDetailsView = ({ appId: propsAppId, orgId: propsOrgId, labName: propsLabName, applicationNo: propsApplicationNo, type: propsType, stage: propsStage }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // State management
  const [assessorsData, setAssessorsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [applicationInfo, setApplicationInfo] = useState(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [selectedAssessorId, setSelectedAssessorId] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // New states for send to DO and finalize assessment
  const [sendToDODialogOpen, setSendToDODialogOpen] = useState(false);
  const [finalizeDialogOpen, setFinalizeDialogOpen] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [success, setSuccess] = useState(null);

  // States for Generate AAFee functionality
  const [generateAAFeeConfirmOpen, setGenerateAAFeeConfirmOpen] = useState(false);
  const [generateAAFeeDialogOpen, setGenerateAAFeeDialogOpen] = useState(false);
  const [disciplineListAAFee, setDisciplineListForAAFee] = useState([]);
  const [selectedDisciplines, setSelectedDisciplines] = useState([]);
  const [aaFeeGenerated, setAAFeeGenerated] = useState(false);
  const [loadingDisciplines, setLoadingDisciplines] = useState(false);

  // States for CAB Details
  const [cabDetails, setCabDetails] = useState(null);
  const [labType, setLabType] = useState('');
  const [CabType, setCabType] = useState('');
  const [labTypeId, setLabTypeId] = useState(null);
  const [noOfCollectionCenter, setNoOfCollectionCenter] = useState(0);
  const [accreditationCategory, setAccreditationCategory] = useState('');

  // State for editable collection center count in AAFee dialog
  const [editableCollectionCenters, setEditableCollectionCenters] = useState(0);

  // Allocated assessors for display
  const [allocatedAssessors, setAllocatedAssessors] = useState([]);
  const [disciplineList, setDisciplineList] = useState([]);
  const [acceptDialogOpen, setAcceptDialogOpen] = useState(false);
  const [showAAFeeButton, setShowAAFeeButton] = useState(false);

  // PT Plan states
  const [uploadedPtPlanFile, setUploadedPtPlanFile] = useState(null);
  const [ptPlanUploaded, setPtPlanUploaded] = useState(false);

  // Form 11 request approval state
  const [form11Approved, setForm11Approved] = useState(false);


  const handleViewDeclaration = (assessor) => {
    setSelectedAssessorId(assessor.id);
    setAcceptDialogOpen(true);
  };
  const handlecloseDialogViewdeclaration = (app) => {
    setAcceptDialogOpen(false);
  };

  // Determine whether to use props or URL params
  let appId, orgId, labName, applicationNo, type, stage;

  if (propsAppId && propsOrgId) {
    // Use props if available (when component is used as child)
    console.log("Using stage from props:", propsStage);
    try {
      appId = decryptText(propsAppId);
      orgId = decryptText(propsOrgId);
      type = propsType ? propsType : null;
      stage = propsStage ? propsStage : null;
      labName = propsLabName ? decryptText(propsLabName) : null;
      applicationNo = propsApplicationNo ? decryptText(propsApplicationNo) : null;
    } catch (err) {
      console.error('Error decrypting props:', err);
    }
  } else {
    // Fallback to URL params (when component is used as standalone page)
    const searchParams = new URLSearchParams(location.search);
    const encryptedAppId = searchParams.get('appId');
    const encryptedOrgId = searchParams.get('orgId');
    const encryptedLabName = searchParams.get('labName');
    const encryptedApplicationNo = searchParams.get('applicationNo');

    try {
      if (encryptedAppId) {
        appId = decryptText(encryptedAppId);
      }
      if (encryptedOrgId) {
        orgId = decryptText(encryptedOrgId);
      }
      if (encryptedLabName) {
        labName = decryptText(encryptedLabName);
      }
      if (encryptedApplicationNo) {
        applicationNo = decryptText(encryptedApplicationNo);
      }
    } catch (err) {
      console.error('Error decrypting URL parameters:', err);
    }
  }

  // Get user data from localStorage
  const userData = JSON.parse(localStorage.getItem('userData') || '{}');


  // Function to fetch saved assessors for all disciplines
  const fetchSavedAssessors = async (disciplines) => {
    if (!disciplines || disciplines.length === 0) return;

    try {
      const allAssessors = [];

      for (const discipline of disciplines) {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/assessor/GetSavedAssessors?appid=${appId}&disciplineid=${discipline.id}`
        );

        if (response.data && response.data.data && Array.isArray(response.data.data)) {
          const disciplineAssessors = response.data.data.map(assessor => ({
            id: assessor.Id || assessor.id || `${discipline.id}-${assessor.AssessorId}`,
            assessorId: assessor.AssessorId || assessor.assessorId,
            assessor: assessor.AssessorName || assessor.Name || 'Unknown Assessor',
            capacity: assessor.Capacity || 'N/A',
            discipline: discipline.name,
            disciplineId: discipline.id,
            startDate: assessor.StartDate || assessor.FromDate || '',
            endDate: assessor.EndDate || assessor.ToDate || '',
            Discipline: assessor.Discipline || discipline.name
          }));
          allAssessors.push(...disciplineAssessors);
        }
      }

      setAllocatedAssessors(allAssessors);
    } catch (error) {
      console.error('Error fetching saved assessors:', error);
    }
  };

  // Fetch Form 11 request approval status
  useEffect(() => {
    if (!appId) return;
    axios.get(
      `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/do/request-form11-status`,
      { params: { appId } }
    ).then(res => {
      if (res.data.success && res.data.isApproved) setForm11Approved(true);
    }).catch(() => {});
  }, [appId]);

  // Load assessors data
  useEffect(() => {
    if (appId) {
      loadAssessors();
      checkAAFeeAPI();
      fetchPTPlan();

      // Load CAB details but don't fail if it doesn't work
      getCABDetails().catch(error => {
        console.warn('Failed to load CAB details on mount:', error.message);
        // Set defaults
        setLabType('Medical');
        setNoOfCollectionCenter(0);
        setAccreditationCategory('');
      });
    }
  }, [appId]);

  const loadAssessors = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await JDApiService.getAssessors({ appId });
      console.log('Assessors API response:', data);

      if (data.success) {
        let assessors = [];

        if (data.data && data.data.data) {
          assessors = Array.isArray(data.data.data) ? data.data.data : [];
        } else if (data.data) {
          assessors = Array.isArray(data.data) ? data.data : [];
        }
        setAssessorsData(assessors);
      } else {
        throw new Error(data.message || 'Failed to load assessors');
      }
    } catch (err) {
      console.error('Load assessors error:', err);
      setError(`Error loading assessors: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Get status chip component
  const getStatusChip = (status, label) => {
    let color = 'default';
    let icon = <HourglassEmptyIcon fontSize="small" />;

    if (status === 1) {
      color = 'success';
      icon = <CheckCircleIcon fontSize="small" />;
    } else if (status === 2) {
      color = 'error';
      icon = <CancelIcon fontSize="small" />;
    } else {
      color = 'warning';
    }

    return (
      <Chip
        label={status === 1 ? 'Accepted' : status === 2 ? 'Rejected' : 'Pending'}
        size="small"
        color={color}
        icon={icon}
      />
    );
  };

  // Handle Accept Assessor
  const handleAccept = async (assessorId) => {
    setActionLoading(true);
    setError(null);

    try {
      const response = await JDApiService.acceptAssessor({
        appId,
        assessorId,
        orgId,
        userTypeId: userData.userTypeId
      });

      if (response.success) {
        // Reload assessors data
        await loadAssessors();
        setError(null);
      } else {
        setError(response.message || 'Failed to accept assessor');
      }
    } catch (err) {
      console.error('Accept assessor error:', err);
      setError(`Error accepting assessor: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle Accept All Assessors
  const handleAcceptAll = async () => {
    setActionLoading(true);
    setError(null);

    try {
      // Filter pending assessors
      const pendingAssessors = assessorsData.filter(assessor => {
        const jdAccepted = Array.isArray(assessor.JDirectorAccepted)
          ? assessor.JDirectorAccepted[0]
          : assessor.JDirectorAccepted;
        return jdAccepted === 0 || jdAccepted === null;
      });

      if (pendingAssessors.length === 0) {
        setError('No pending assessors to accept');
        return;
      }

      let successCount = 0;
      let failureCount = 0;
      const failedAssessors = [];

      // Process each assessor sequentially
      for (const assessor of pendingAssessors) {
        const assessorId = Array.isArray(assessor.Id)
          ? assessor.Id[0]
          : assessor.Id;

        try {
          const response = await JDApiService.acceptAssessor({
            appId,
            assessorId,
            orgId,
            userTypeId: userData.userTypeId
          });

          if (response.success) {
            successCount++;
          } else {
            failureCount++;
            failedAssessors.push(assessor.AssessorName || `Assessor ${assessorId}`);
          }
        } catch (err) {
          console.error(`Error accepting assessor ${assessorId}:`, err);
          failureCount++;
          failedAssessors.push(assessor.AssessorName || `Assessor ${assessorId}`);
        }
      }

      // Show results
      if (successCount > 0) {
        setSuccess(`Successfully accepted ${successCount} assessor${successCount > 1 ? 's' : ''}`);
      }

      if (failureCount > 0) {
        setError(`Failed to accept ${failureCount} assessor${failureCount > 1 ? 's' : ''}: ${failedAssessors.join(', ')}`);
      }

      // Reload assessors data
      await loadAssessors();
    } catch (err) {
      console.error('Accept all assessors error:', err);
      setError(`Error accepting assessors: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle Reject Assessor - Open dialog
  const handleRejectClick = (assessorId) => {
    setSelectedAssessorId(assessorId);
    setRejectReason('');
    setRejectDialogOpen(true);
  };

  // Handle Reject Assessor - Confirm
  const handleRejectConfirm = async () => {
    if (!rejectReason.trim()) {
      setError('Please provide a reason for rejection');
      return;
    }

    setActionLoading(true);
    setError(null);
    console.log('Rejecting assessor with ID:', selectedAssessorId, 'Reason:', rejectReason);

    try {
      const response = await JDApiService.rejectAssessor({
        appId,
        assessorId: selectedAssessorId,
        remarks: rejectReason,
        orgId,
        userTypeId: userData.userTypeId
      });

      if (response.success) {
        // Close dialog and reload assessors data
        setRejectDialogOpen(false);
        setRejectReason('');
        setSelectedAssessorId(null);
        await loadAssessors();
        setError(null);
      } else {
        setError(response.message || 'Failed to reject assessor');
      }
    } catch (err) {
      console.error('Reject assessor error:', err);
      setError(`Error rejecting assessor: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle back button
  const handleBack = () => {
    navigate(-1);
  };

  // Check if there are any pending assessors for JD
  const hasPendingAssessors = () => {
    if (!assessorsData || assessorsData.length === 0) return false;
    return assessorsData.some(assessor => {
      const jDirectorAccepted = Array.isArray(assessor.JDirectorAccepted)
        ? assessor.JDirectorAccepted[0]
        : assessor.JDirectorAccepted;
      return jDirectorAccepted === 0 || jDirectorAccepted === null;
    });
  };

  // Check if all assessors are accepted
  const areAllAssessorsAccepted = () => {
    if (!assessorsData || assessorsData.length === 0) return false;
    return assessorsData.every(assessor => {
      const jDirectorAccepted = Array.isArray(assessor.JDirectorAccepted)
        ? assessor.JDirectorAccepted[0]
        : assessor.JDirectorAccepted;
      return jDirectorAccepted === 1;
    });
  };

  // Check if any assessor is rejected
  const isAnyAssessorRejected = () => {
    if (!assessorsData || assessorsData.length === 0) return false;
    return assessorsData.some(assessor => {
      const jDirectorAccepted = Array.isArray(assessor.JDirectorAccepted)
        ? assessor.JDirectorAccepted[0]
        : assessor.JDirectorAccepted;
      return jDirectorAccepted === 2;
    });
  };

  // Handle Send to Dealing Officer
  const handleSendToDealingOfficer = () => {
    setConfirmPassword('');
    setSendToDODialogOpen(true);
  };

  // Confirm Send to Dealing Officer
  const confirmSendToDealingOfficer = async () => {
    if (!confirmPassword || confirmPassword.trim() === '') {
      setError('Please enter your password');
      return;
    }

    setActionLoading(true);
    setError(null);
    const jdOrgId = localStorage.getItem('orgId');
    try {
      // Verify password first
      const verifyResult = await authService.verifyPassword(jdOrgId, confirmPassword);

      if (!verifyResult.success) {
        setError('Invalid password. Please try again.');
        setActionLoading(false);
        return;
      }

      // Password verified, proceed with sending to dealing officer
      const data = await JDApiService.sendToDealingOfficer({
        appId,
        orgId,
        userTypeId: userData.userTypeId,
        password: confirmPassword
      });

      if (data.success) {
        setSuccess('Application sent to dealing officer successfully!');
        setSendToDODialogOpen(false);
        setConfirmPassword('');
        // Navigate back after success
        setTimeout(() => {
          navigate(-1);
        }, 1500);
      } else {
        setError(data.message || 'Failed to send to dealing officer');
      }
    } catch (err) {
      console.error('Send to dealing officer error:', err);
      setError(`Failed to send to dealing officer: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle Finalize Assessment
  const handleFinalizeAssessment = () => {
    setConfirmPassword('');
    setFinalizeDialogOpen(true);
  };

  // Confirm Finalize Assessment
  const confirmFinalizeAssessment = async () => {
    if (!confirmPassword || confirmPassword.trim() === '') {
      setError('Please enter your password');
      return;
    }

    setActionLoading(true);
    setError(null);
    const jdOrgId = localStorage.getItem('orgId');
    try {
      // Verify password first
      const verifyResult = await authService.verifyPassword(jdOrgId, confirmPassword);

      if (!verifyResult.success) {
        setError('Invalid password. Please try again.');
        setActionLoading(false);
        return;
      }

      // Password verified, proceed with finalizing assessment
      const data = await JDApiService.finalizeAssessment({
        appId,
        orgId,
        userTypeId: userData.userTypeId,
        password: confirmPassword
      });

      if (data.success) {
        setSuccess('Assessment finalized successfully!');
        setFinalizeDialogOpen(false);
        setConfirmPassword('');
        // Navigate back after success
        setTimeout(() => {
          navigate(-1);
        }, 1500);
      } else {
        setError(data.message || 'Failed to finalize assessment');
      }
    } catch (err) {
      console.error('Finalize assessment error:', err);
      setError(`Failed to finalize assessment: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  // Accounts Details button handler
  const handleAccountsDetailsClick = () => {
    if (!appId) {
      console.error('AppId not available for accounts details');
      return;
    }

    try {
      // Encrypt the parameters (assuming we need both appId as id and assessment type)
      const encryptedIdParam = encryptText(appId.toString());
      const encryptedTypeParam = encryptText('Main'); // Default to Main assessment type

      // Navigate to accounts details with encrypted parameters
      navigate(`/main/do/accounts-details?id=${encryptedIdParam}&type=${encryptedTypeParam}`);
    } catch (error) {
      console.error('Error encrypting parameters for accounts details:', error);
    }
  };

  // API function to get discipline list for AA Fee (equivalent to mode 4)
  const getDisciplinesForAAFee = async () => {
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/aafee/get-disciplines`, {
        mode: 4,
        appId: appId
      });

      if (response.data.success && response.data.data) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to get disciplines');
      }
    } catch (error) {
      console.error('Error fetching disciplines for AA Fee:', error);
      throw error;
    }
  };

  // API function to generate AAFee (equivalent to mode 5)
  const generateAAFeeAPI = async (disciplineIds) => {
    try {
      // Use the editable collection center count instead of the original one
      const finalCollectionCenterCount = editableCollectionCenters;

      // Determine collection center categories based on editableCollectionCenters
      // But also respect the accreditationCategory from API if available
      let smallLab = 0, mediumLab = 0, largeLab = 0, veryLargeLab = 0;

      // If we have an accreditationCategory from API, use it to determine lab size
      if (accreditationCategory) {
        const categoryLower = accreditationCategory.toLowerCase();
        if (categoryLower.includes('small')) {
          smallLab = 1;
        } else if (categoryLower.includes('medium')) {
          mediumLab = 1;
        } else if (categoryLower.includes('large') && !categoryLower.includes('very')) {
          largeLab = 1;
        } else if (categoryLower.includes('very large')) {
          veryLargeLab = 1;
        } else {
          // Default to medium if category is present but doesn't match known patterns
          mediumLab = 1;
        }
      } else {
        // Fallback to editable collection center count if no accreditation category
        if (finalCollectionCenterCount <= 10) {
          smallLab = 1;
        } else if (finalCollectionCenterCount <= 25) {
          mediumLab = 1;
        } else if (finalCollectionCenterCount <= 50) {
          largeLab = 1;
        } else {
          veryLargeLab = 1;
        }
      }

      const response = await axios.post(`${process.env.REACT_APP_API_URL}/aafee/generate`, {
        mode: 5,
        appId: appId,
        orgId: orgId,
        disciplineIds: disciplineIds.join(','),
        labType: labType,
        noOfCollectionCenter: finalCollectionCenterCount, // Use editable value
        accreditationCategory: accreditationCategory,
        // Collection center categorization for ac_process table update
        smallLab: smallLab,
        mediumLab: mediumLab,
        largeLab: largeLab,
        veryLargeLab: veryLargeLab
      });

      if (response.data.success) {
        // Use accreditation category if available, otherwise calculate from collection centers
        let categoryText = accreditationCategory;
        if (!categoryText) {
          categoryText = finalCollectionCenterCount <= 10 ? 'Small Lab' :
            finalCollectionCenterCount <= 25 ? 'Medium Lab' :
              finalCollectionCenterCount <= 50 ? 'Large Lab' : 'Very Large Lab';
        }

        alert(`AAFe generated successfully for ${labType} laboratory (${categoryText}) with ${finalCollectionCenterCount} collection centers`);
        await checkAAFeeAPI();
        return response.data;
      } else {
        throw new Error(response.data.message || 'Failed to generate AAFe');
      }
    } catch (error) {
      console.error('Error generating AAFe:', error);
      throw error;
    }
  };
  const checkAAFeeAPI = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/aafee/details?appId=${appId}&orgId=${orgId}`);
      if (response.data.show == true) {
        setShowAAFeeButton(true);
      } else {
        setShowAAFeeButton(false);
      }
    } catch (error) {
      console.error('Error generating AAFe:', error);
      setShowAAFeeButton(false);
      throw error;
    }
  };

  // API function to get CAB details including lab type, collection centers, and accreditation category
  const getCABDetails = async () => {
    if (!appId) {
      throw new Error('AppId is required for CAB details');
    }

    try {
      console.log('Getting CAB details for appId:', appId);
      console.log('API Base URL:', process.env.REACT_APP_API_URL);

      // Try DoApiService first
      let response;
      try {
        response = await DoApiService.getCABDetails({
          appId: parseFloat(appId),
        });
        console.log('DoApiService response:', response);
      } catch (serviceError) {
        console.warn('DoApiService failed, trying direct axios call:', serviceError.message);

        // Fallback to direct axios call
        const axiosResponse = await axios.get(`${process.env.REACT_APP_API_URL}/do/applications/${appId}/cab-details`);
        response = axiosResponse.data;
        console.log('Direct axios response:', response);
      }

      if (response && response.success && response.data) {
        const cabData = response.data;
        setCabDetails(cabData);

        // Set specific fields needed for AAFee generation
        const extractedLabType = cabData.labType || 'Medical';
        const extractedCabType = cabData.CabType || '';
        const extractedNoOfCollectionCenter = cabData.noOfCollectionCenter || 0;
        const extractedAccreditationCategory = cabData.accreditationCategory || '';

        setLabType(extractedLabType);
        setCabType(extractedCabType);
        setLabTypeId(cabData.labTypeId || null);
        setNoOfCollectionCenter(extractedNoOfCollectionCenter);
        setAccreditationCategory(extractedAccreditationCategory);

        console.log('CAB Details extracted and set:', {
          labType: extractedLabType,
          noOfCollectionCenter: extractedNoOfCollectionCenter,
          accreditationCategory: extractedAccreditationCategory,
          fullCabData: cabData
        });

        return cabData;
      } else {
        console.error('CAB Details API returned unsuccessful response:', response);
        throw new Error(response?.message || 'Failed to get CAB details - API returned unsuccessful response');
      }
    } catch (error) {
      console.error('Error fetching CAB details:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        appId: appId
      });

      // Set default values if CAB details fail to load
      setLabType('Medical');
      setNoOfCollectionCenter(0);
      setAccreditationCategory('');

      throw new Error(`Failed to get CAB details: ${error.message}`);
    }
  };

  // Handle Generate AAFee button click (first confirmation)
  const handleGenerateAAFeeClick = () => {
    setGenerateAAFeeConfirmOpen(true);
  };

  // Handle first confirmation - if Yes, open discipline selection dialog
  const handleGenerateAAFeeConfirm = async () => {
    setGenerateAAFeeConfirmOpen(false);
    setLoadingDisciplines(true);

    try {
      // Try to load CAB details first, but don't fail if it doesn't work
      try {
        await getCABDetails();
        console.log('CAB details loaded successfully');
      } catch (cabError) {
        console.warn('Failed to load CAB details, using defaults:', cabError.message);
        // Set defaults if CAB details loading fails
        setLabType('Medical');
        setNoOfCollectionCenter(0);
        setAccreditationCategory('');
      }

      const disciplines = await getDisciplinesForAAFee();
      setDisciplineListForAAFee(disciplines);

      // Check if AA Fee is already generated (you might need to add this check to your API)
      // For now, assuming it's not generated
      setAAFeeGenerated(false);
      setSelectedDisciplines(aaFeeGenerated ? disciplines.map(d => d.DisciplineId) : []);

      // Initialize editable collection centers with current value
      setEditableCollectionCenters(noOfCollectionCenter);

      setGenerateAAFeeDialogOpen(true);
    } catch (error) {
      setError(`Error loading disciplines: ${error.message}`);
    } finally {
      setLoadingDisciplines(false);
    }
  };

  // Handle discipline checkbox change
  const handleDisciplineChange = (disciplineId, checked) => {
    if (aaFeeGenerated) return; // Don't allow changes if already generated

    setSelectedDisciplines(prev => {
      if (checked) {
        return [...prev, disciplineId];
      } else {
        return prev.filter(id => id !== disciplineId);
      }
    });
  };

  // Handle collection center count change
  const handleCollectionCenterChange = (event) => {
    const value = parseInt(event.target.value) || 0;
    setEditableCollectionCenters(Math.max(0, value)); // Ensure non-negative
  };

  // Helper function to get lab category based on collection center count
  const getLabCategoryFromCount = (count) => {
    if (count <= 10) return 'Small Lab';
    if (count <= 25) return 'Medium Lab';
    if (count <= 50) return 'Large Lab';
    return 'Very Large Lab';
  };

  // Handle final AA Fee generation submit
  const handleAAFeeSubmit = async () => {
    if ((labType !== "Medical"  || CabType === 'Medical Imaging') && selectedDisciplines.length === 0) {
      setError('Please select at least one discipline');
      return;
    }

    setActionLoading(true);
    setError(null);

    try {
      await generateAAFeeAPI(selectedDisciplines);
      // setSuccess('AA Fee Generated Successfully');
      setGenerateAAFeeDialogOpen(false);
      setAAFeeGenerated(true);

      // Optional: Refresh assessors data or redirect
      // navigate('/main/dashboard');
    } catch (error) {
      setError(`Error generating AA Fee: ${error.message}`);
    } finally {
      setActionLoading(false);
    }
  };
  const handleTADAClick = (assessor) => {
    if (!appId) {
      console.error('AppId not available for TADA details');
      return;
    }

    try {
      // Get assessorId properly (handle array case)
      const assessorId = Array.isArray(assessor.AllotedToId) ? assessor.AllotedToId[0] : assessor.AllotedToId;

      // Encrypt the parameters using the correct values
      const encryptedAppIdParam = encryptText(appId.toString());
      const encryptedOrgIdParam = encryptText(orgId.toString());
      const encryptedAssessmentIdParam = encryptText(assessor.AssessorAllotmentId.toString());
      const encryptedAssessorIdParam = encryptText(assessorId.toString());

      // Navigate to TADA page with encrypted parameters
      navigate(`/main/do/TADA?appId=${encryptedAppIdParam}&orgId=${encryptedOrgIdParam}&assessmentId=${encryptedAssessmentIdParam}&assessorId=${encryptedAssessorIdParam}`);
    } catch (error) {
      console.error('Error encrypting parameters for TADA details:', error);
    }
  };

  // PT Plan Functions
  const fetchPTPlan = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/Getptplan`,
        {
          params: { appid: appId }
        }
      );
      if (response.data && response.data.file) {
        setUploadedPtPlanFile(response.data.file);
        setPtPlanUploaded(true);
      } else {
        setUploadedPtPlanFile(null);
        setPtPlanUploaded(false);
      }
    } catch (err) {
      console.error('Error fetching PT Plan:', err);
      setUploadedPtPlanFile(null);
      setPtPlanUploaded(false);
    }
  };

  const handleViewPTPlan = async () => {
    if (!uploadedPtPlanFile) {
      showSnackbar('PT Plan file not found', 'error');
      return;
    }
    try {
      setLoading(true);
      // Use s3Utils to handle file opening with signed URLs
      await s3Utils.handleFileViewWithS3(uploadedPtPlanFile, {
        showError: (msg) => setError(msg),
        showLoading: () => setLoading(true),
        hideLoading: () => setLoading(false),
        expiresIn: 172800, // 48 hours
      });
    } catch (err) {
      setError("Failed to open file");
      console.error("Error opening file:", err);
    } finally {
      setLoading(false);
    }
  };

  // Snackbar helper function
  const showSnackbar = (message, severity = "success") => {
    if (severity === "success") {
      setSuccess(message);
    } else {
      setError(message);
    }
  };

  const handleApplicationChecklist = (appId, assessmentId, orgId) => {
    const url =
      "/main/do/nc-application" +
      "?appId=" + encodeURIComponent(encryptAppId(appId)) +
      "&assessmentId=" + encodeURIComponent(encryptText(assessmentId)) +
      "&orgId=" + encodeURIComponent(encryptText(orgId));
    window.open(url, "_blank"); // ✅ open in new tab
  };

  const handleForm11 = async (assessor) => {
    const response = await CommonAPIService.getAssessmentSendToNABLDate(appId);
    if (response.success && response.data) {
      if (response.data.isDisplayNew) {
        const url = `/main/do/form11-new?appId=${encryptAppId(
          appId,
        )}&orgId=${encryptText(orgId)}&assessmentId=${encryptText(
          assessor.AssessorAllotmentId,
        )}&assessorId=${encryptText(assessor.AllotedToId)}`;
        navigate(url);
      } else {
        const url = `/main/do/form11?appId=${encryptAppId(
          appId,
        )}&orgId=${encryptText(orgId)}&assessmentId=${encryptText(
          assessor.AssessorAllotmentId,
        )}&assessorId=${encryptText(assessor.AllotedToId)}`;
        navigate(url);
      }
    }
  };


  // Early validation - don't render if required data is missing
  if (!appId || !orgId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Missing required application data. AppId: {appId ? '✓' : '✗'}, OrgId: {orgId ? '✓' : '✗'}
          <br />
          Props received: {JSON.stringify({ propsAppId, propsOrgId })}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header with Back Button */}
      <Paper elevation={2} sx={{ p: 2, mt: 2, background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box>
              <Typography variant="h4" component="h1" sx={{ color: 'white', fontWeight: 600 }}>
                Assessment Details
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', mt: 0.5 }}>
                View and manage assessment information
              </Typography>
            </Box>
          </Box>
          <PersonIcon sx={{ fontSize: 48, color: 'rgba(255,255,255,0.3)' }} />
        </Box>
      </Paper>
      <Dialog
        open={acceptDialogOpen}
        onClose={handleViewDeclaration}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Assessor Accepted Declaration</DialogTitle>
        <DialogContent>
          <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', fontWeight: 'bold' }}>
            I hereby declare the following:
          </Typography>

          <Box sx={{ mb: 3 }}>

            <Typography variant="body1">
              <Box component="span" sx={{ fontWeight: 'bold', mr: 1 }}>1.</Box>
              I have not offered any consultancy, guidance, supervision or other services to the CAB (e.g. internal audit), in any way.
            </Typography>


            <Typography variant="body1">
              <Box component="span" sx={{ fontWeight: 'bold', mr: 1 }}>2.</Box>
              I got an opportunity to go through various documents like Quality Manual, Procedural Manuals, Work instructions, Internal reports etc. of the above CAB and other related information that might have been given by NABL. I undertake to maintain strict confidentiality of the information acquired in course of discharge of my responsibility and shall not disclose to any person other than that required by NABL.
            </Typography>



            <Typography variant="body1">
              <Box component="span" sx={{ fontWeight: 'bold', mr: 1 }}>3.</Box>
              I am not an Ex Employee of this CAB.
            </Typography>

            <Typography variant="body1">
              <Box component="span" sx={{ fontWeight: 'bold', mr: 1 }}>4.</Box>
              I am not in relation with any person of the management of this CAB.
            </Typography>

          </Box>

        </DialogContent>
        <DialogActions>
          <Button
            onClick={handlecloseDialogViewdeclaration}
            disabled={loading}
            variant="outlined"
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Action Buttons */}
      {!loading && assessorsData.length > 0 && (
        <Box sx={{ mb: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          {/* Show Accept All button if there are pending assessors and user is JD */}
          {type === "JD" && hasPendingAssessors() && (
            <Button
              onClick={handleAcceptAll}
              variant="contained"
              color="success"
              disabled={actionLoading}
              startIcon={<CheckIcon />}
              size="large"
              sx={{ fontWeight: 600 }}
            >
              Accept All Assessors
            </Button>
          )}



          {/* Show Send to Dealing Officer button if any assessor is rejected */}
          {isAnyAssessorRejected() && (
            <Button
              onClick={handleSendToDealingOfficer}
              variant="contained"
              color="warning"
              disabled={actionLoading}
              startIcon={<SendIcon />}
              size="large"
              sx={{ fontWeight: 600 }}
            >
              Send to Dealing Officer
            </Button>
          )}

          {/* Show Final Assessment Scheduled button if all assessors are accepted */}
          {areAllAssessorsAccepted() && type === "JD" && stage === "19b" && (
            <Button
              onClick={handleFinalizeAssessment}
              variant="contained"
              color="success"
              disabled={actionLoading}
              startIcon={<CheckIcon />}
              size="large"
              sx={{ fontWeight: 600 }}
            >
              Schedule Final Assessment
            </Button>
          )}
        </Box>
      )}

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Assessors Table */}
      {!loading && assessorsData.length > 0 && (
        <TableContainer
          component={Paper}
          sx={{
            boxShadow: 4,
            borderRadius: 2,
            overflow: 'auto',
            maxHeight: '600px'
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#1976d2', color: 'white', minWidth: 250, fontSize: '0.9rem' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <DescriptionIcon fontSize="small" />
                    Action Center
                  </Box>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#1976d2', color: 'white', minWidth: 200, fontSize: '0.9rem' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <PersonIcon fontSize="small" />
                    Assessor Details
                  </Box>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#1976d2', color: 'white', fontSize: '0.9rem' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <AssessmentIcon fontSize="small" />
                    Assessment Status & Capacity
                  </Box>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#1976d2', color: 'white', minWidth: 150, fontSize: '0.9rem' }}>
                  {labTypeId === 1 || labTypeId === 2 ? "Discipline/Group" : "Discipline" }
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#1976d2', color: 'white', minWidth: 200, fontSize: '0.9rem' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <DateRangeIcon fontSize="small" />
                    Assessment Period
                  </Box>
                </TableCell>
                {type !=="LAB" && (
                  <>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#1976d2', color: 'white', fontSize: '0.9rem' }}>
                  Assessor Status
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#1976d2', color: 'white', fontSize: '0.9rem' }}>
                  CAB Status
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#1976d2', color: 'white', fontSize: '0.9rem' }}>
                  JD Status
                </TableCell>
                  </>
                )}
              
                {type === 'JD' && (
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#1976d2', color: 'white', fontSize: '0.9rem' }}>
                    JD Actions
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {assessorsData.map((assessor, index) => {
                // Handle array fields
                const assessorAccepted = Array.isArray(assessor.AssessorAccepted)
                  ? assessor.AssessorAccepted[0]
                  : assessor.AssessorAccepted;

                const cabAccepted = Array.isArray(assessor.CabAccepted)
                  ? assessor.CabAccepted[0]
                  : assessor.CabAccepted;

                const jdAccepted = Array.isArray(assessor.JDirectorAccepted)
                  ? assessor.JDirectorAccepted[0]
                  : assessor.JDirectorAccepted;

                const assessorId = Array.isArray(assessor.Id)
                  ? assessor.Id[0]
                  : assessor.Id;

                return (
                  <TableRow
                    key={assessorId || index}
                    sx={{
                      "&:nth-of-type(odd)": {
                        backgroundColor: "#fafafa",
                      },
                      "&:hover": {
                        backgroundColor: "#e3f2fd",
                        transition: "background-color 0.3s",
                      },
                    }}
                  >
                    {/* Action Center - First Column */}
                    <TableCell>
                      {type !== "LAB" ? (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                            alignItems: "flex-start",
                          }}
                        >
                          {(stage === "21" ||
                            stage === "6a" ||
                            stage === "6b" ||
                            stage === "6d" ||
                            stage === "6e" ||
                            stage === "6f" ||
                            stage === "7" ||
                            stage === "7a" ||
                            stage === "7b" ||
                            stage === "7c" ||
                            stage === "7d" ||
                            stage === "7e" ||
                            stage === "7f" ||
                            stage === "7g" ||
                            stage === "7h" ||
                            stage === "11b" ||
                            stage === "12" ||
                            stage === "12a" ||
                            stage === "12b" ||
                            stage === "13" ||
                            stage === "13a" ||
                            stage === "13b" ||
                            stage === "13c" ||
                            stage === "13d" ||
                            stage === "13e" ||
                            stage === "11f" ||
                            stage === "11g" ||
                            stage === "13") && (
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => {
                                const url = `/main/do/nc-reply?appId=${encryptAppId(
                                  assessor.AppId,
                                )}&assessmentId=${encryptText(
                                  assessor.AssessorAllotmentId,
                                )}&assessorId=${encryptText(
                                  assessor.AllotedToId,
                                )}&orgId=${encryptText(orgId)}`;
                                window.open(
                                  url,
                                  "_blank",
                                  "noopener,noreferrer",
                                );
                              }}
                              sx={{
                                background: `linear-gradient(135deg, ${colors.success.main}, ${colors.success.dark})`,
                                color: "white",
                                px: 2,
                                py: 0.5,
                                borderRadius: 3,
                                fontWeight: 500,
                                fontSize: "0.7rem",
                                textTransform: "none",
                                minWidth: "140px",
                                boxShadow: `0 4px 16px ${alpha(
                                  colors.success.main,
                                  0.3,
                                )}`,
                                "&:hover": {
                                  boxShadow: `0 8px 24px ${alpha(
                                    colors.success.main,
                                    0.4,
                                  )}`,
                                  transform: "translateY(-2px)",
                                  background: `linear-gradient(135deg, ${colors.success.dark}, ${colors.success.main})`,
                                },
                                transition: "all 0.3s ease",
                              }}
                            >
                              NC Clarification
                            </Button>
                          )}

                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => handleTADAClick(assessor)}
                            sx={{
                              background: `linear-gradient(135deg, ${colors.warning.main}, ${colors.warning.dark})`,
                              color: "white",
                              px: 2,
                              py: 0.5,
                              borderRadius: 3,
                              fontWeight: 500,
                              fontSize: "0.7rem",
                              textTransform: "none",
                              minWidth: "140px",
                              boxShadow: `0 4px 16px ${alpha(
                                colors.warning.main,
                                0.3,
                              )}`,
                              "&:hover": {
                                boxShadow: `0 8px 24px ${alpha(
                                  colors.warning.main,
                                  0.4,
                                )}`,
                                transform: "translateY(-2px)",
                              },
                              transition: "all 0.3s ease",
                            }}
                          >
                            Assessor TA DA
                          </Button>

                          {(getCapacityLabel(assessor) === "LA" ||
                            getCapacityLabel(assessor) === "LA_TA") &&
                            (stage === "21" ||
                              stage === "6a" ||
                              stage === "6b" ||
                              stage === "6d" ||
                              stage === "6e" ||
                              stage === "6f" ||
                              stage === "7" ||
                              stage === "7a" ||
                              stage === "7b" ||
                              stage === "7c" ||
                              stage === "7d" ||
                              stage === "7e" ||
                              stage === "7f" ||
                              stage === "7g" ||
                              stage === "7h" ||
                              stage === "11b" ||
                              stage === "12" ||
                              stage === "12a" ||
                              stage === "12b" ||
                              stage === "13" ||
                              stage === "13a" ||
                              stage === "13b" ||
                              stage === "13c" ||
                              stage === "13d" ||
                              stage === "13e" ||
                              stage === "11f" ||
                              stage === "11g" ||
                              stage === "13" ||
                              stage === "6c") && (
                              <>
                                <Button
                                  variant="contained"
                                  size="small"
                                  onClick={() => {
                                    const url = `/main/do/form217-preview?appId=${encryptAppId(
                                      assessor.AppId,
                                    )}&assessmentId=${encryptText(
                                      assessor.AssessorAllotmentId,
                                    )}`;
                                    window.open(
                                      url,
                                      "_blank",
                                      "noopener,noreferrer",
                                    );
                                  }}
                                  sx={{
                                    background: `linear-gradient(135deg, ${colors.primary.main}, ${colors.secondary.main})`,
                                    color: "white",
                                    px: 2,
                                    py: 0.5,
                                    borderRadius: 3,
                                    fontWeight: 500,
                                    fontSize: "0.7rem",
                                    textTransform: "none",
                                    minWidth: "140px",
                                    boxShadow: `0 4px 16px ${alpha(
                                      colors.primary.main,
                                      0.3,
                                    )}`,
                                    "&:hover": {
                                      boxShadow: `0 8px 24px ${alpha(
                                        colors.primary.main,
                                        0.4,
                                      )}`,
                                      transform: "translateY(-2px)",
                                    },
                                    transition: "all 0.3s ease",
                                  }}
                                >
                                  {(labTypeId===1 || labTypeId===2) ? "Assessment Report (NAF 6)" : "View Report"}
                                </Button>

                                <Button
                                  variant="contained"
                                  size="small"
                                  onClick={() => {
                                    const url = `/main/do/nc-report?appId=${encryptAppId(
                                      assessor.AppId,
                                    )}&assessmentId=${encryptText(
                                      assessor.AssessorAllotmentId,
                                    )}`;
                                    window.open(
                                      url,
                                      "_blank",
                                      "noopener,noreferrer",
                                    );
                                  }}
                                  sx={{
                                    background: `linear-gradient(135deg, ${colors.primary.main}, ${colors.secondary.main})`,
                                    color: "white",
                                    px: 2,
                                    py: 0.5,
                                    borderRadius: 3,
                                    fontWeight: 500,
                                    fontSize: "0.7rem",
                                    textTransform: "none",
                                    minWidth: "140px",
                                    boxShadow: `0 4px 16px ${alpha(
                                      colors.primary.main,
                                      0.3,
                                    )}`,
                                    "&:hover": {
                                      boxShadow: `0 8px 24px ${alpha(
                                        colors.primary.main,
                                        0.4,
                                      )}`,
                                      transform: "translateY(-2px)",
                                    },
                                    transition: "all 0.3s ease",
                                  }}
                                >
                                  {(labTypeId===1 || labTypeId===2) ? "Main NC Report (NAF 4)" : "View NC Report"}
                                </Button>

                                <Button
                                  variant="contained"
                                  size="small"
                                  onClick={() =>
                                    handleApplicationChecklist(
                                      assessor.AppId,
                                      assessor.AssessorAllotmentId,
                                      orgId,
                                    )
                                  }
                                  sx={{
                                    background: `linear-gradient(135deg, ${colors.primary.main}, ${colors.secondary.main})`,
                                    color: "white",
                                    px: 2,
                                    py: 0.5,
                                    borderRadius: 3,
                                    fontWeight: 500,
                                    fontSize: "0.7rem",
                                    textTransform: "none",
                                    minWidth: "140px",
                                    boxShadow: `0 4px 16px ${alpha(
                                      colors.primary.main,
                                      0.3,
                                    )}`,
                                    "&:hover": {
                                      boxShadow: `0 8px 24px ${alpha(
                                        colors.primary.main,
                                        0.4,
                                      )}`,
                                      transform: "translateY(-2px)",
                                    },
                                    transition: "all 0.3s ease",
                                  }}
                                >
                                  Application Finding
                                </Button>
                              </>
                            )}

                          {(getCapacityLabel(assessor) === "LA" ||
                            getCapacityLabel(assessor) === "LA_TA") &&
                            (stage === "21" ||
                              stage === "6c" ||
                              stage === "6b" ||
                              stage === "6d" ||
                              stage === "6e" ||
                              stage === "6f" ||
                              stage === "7" ||
                              stage === "7a" ||
                              stage === "7b" ||
                              stage === "7c" ||
                              stage === "7d" ||
                              stage === "7e" ||
                              stage === "7f" ||
                              stage === "7g" ||
                              stage === "7h" ||
                              stage === "11b" ||
                              stage === "12" ||
                              stage === "12a" ||
                              stage === "12b" ||
                              stage === "13" ||
                              stage === "13a" ||
                              stage === "13b" ||
                              stage === "13c" ||
                              stage === "13d" ||
                              stage === "13e" ||
                              stage === "11f" ||
                              stage === "11g" ||
                              stage === "13" ||
                              stage === "6a") && (
                              <>
                                <Button
                                  variant="contained"
                                  size="small"
                                  onClick={handleAccountsDetailsClick}
                                  sx={{
                                    background: `linear-gradient(135deg, ${colors.warning.main}, ${colors.warning.dark})`,
                                    color: "white",
                                    px: 2,
                                    py: 0.5,
                                    borderRadius: 3,
                                    fontWeight: 500,
                                    fontSize: "0.7rem",
                                    textTransform: "none",
                                    minWidth: "140px",
                                    boxShadow: `0 4px 16px ${alpha(
                                      colors.warning.main,
                                      0.3,
                                    )}`,
                                    "&:hover": {
                                      boxShadow: `0 8px 24px ${alpha(
                                        colors.warning.main,
                                        0.4,
                                      )}`,
                                      transform: "translateY(-2px)",
                                    },
                                    transition: "all 0.3s ease",
                                  }}
                                >
                                  Assessor Travel Details
                                </Button>

                                <Button
                                  variant="contained"
                                  size="small"
                                  onClick={()=>handleForm11(assessor)}
                                  sx={{
                                    background: `linear-gradient(135deg, ${colors.secondary.main}, ${colors.secondary.dark})`,
                                    color: "white",
                                    px: 2,
                                    py: 0.5,
                                    borderRadius: 3,
                                    fontWeight: 500,
                                    fontSize: "0.7rem",
                                    textTransform: "none",
                                    minWidth: "140px",
                                    boxShadow: `0 4px 16px ${alpha(
                                      colors.secondary.main,
                                      0.3,
                                    )}`,
                                    "&:hover": {
                                      boxShadow: `0 8px 24px ${alpha(
                                        colors.secondary.main,
                                        0.4,
                                      )}`,
                                      transform: "translateY(-2px)",
                                    },
                                    transition: "all 0.3s ease",
                                  }}
                                >
                                  Form 11
                                </Button>
                                {labTypeId===3 && (
                                <Button
                                  variant="contained"
                                  size="small"
                                  onClick={() => {
                                    const url = `/main/do/form72?appId=${encodeURIComponent(
                                      encryptAppId(appId),
                                    )}&orgId=${encodeURIComponent(
                                      encryptText(orgId),
                                    )}&assessmentId=${encodeURIComponent(
                                      encryptText(assessor.AssessorAllotmentId),
                                    )}&assessorId=${encodeURIComponent(
                                      encryptText(assessorId),
                                    )}`;

                                    window.open(
                                      url,
                                      "_blank",
                                      "noopener,noreferrer",
                                    );
                                  }}
                                  sx={{
                                    background: `linear-gradient(135deg, ${colors.secondary.main}, ${colors.secondary.dark})`,
                                    color: "white",
                                    px: 2,
                                    py: 0.5,
                                    borderRadius: 3,
                                    fontWeight: 500,
                                    fontSize: "0.7rem",
                                    textTransform: "none",
                                    minWidth: "140px",
                                    boxShadow: `0 4px 16px ${alpha(
                                      colors.secondary.main,
                                      0.3,
                                    )}`,
                                    "&:hover": {
                                      boxShadow: `0 8px 24px ${alpha(
                                        colors.secondary.main,
                                        0.4,
                                      )}`,
                                      transform: "translateY(-2px)",
                                    },
                                    transition: "all 0.3s ease",
                                  }}
                                >
                                  Form 72
                                </Button>
                                )}
                                 {(labTypeId===1 || labTypeId===2)&&(
                                <Button
                                  variant="contained"
                                  size="small"
                                  onClick={() => {
                                    const url = `/main/do/form73-calibration?appId=${encodeURIComponent(
                                      encryptAppId(appId),
                                    )}&orgId=${encodeURIComponent(
                                      encryptText(orgId),
                                    )}&assessmentId=${encodeURIComponent(
                                      encryptText(assessor.AssessorAllotmentId),
                                    )}&assessorId=${encodeURIComponent(
                                      encryptText(assessorId),
                                    )}`;

                                    window.open(
                                      url,
                                      "_blank",
                                      "noopener,noreferrer",
                                    );
                                  }}
                                  sx={{
                                    background: `linear-gradient(135deg, ${colors.secondary.main}, ${colors.secondary.dark})`,
                                    color: "white",
                                    px: 2,
                                    py: 0.5,
                                    borderRadius: 3,
                                    fontWeight: 500,
                                    fontSize: "0.7rem",
                                    textTransform: "none",
                                    minWidth: "140px",
                                    boxShadow: `0 4px 16px ${alpha(
                                      colors.secondary.main,
                                      0.3,
                                    )}`,
                                    "&:hover": {
                                      boxShadow: `0 8px 24px ${alpha(
                                        colors.secondary.main,
                                        0.4,
                                      )}`,
                                      transform: "translateY(-2px)",
                                    },
                                    transition: "all 0.3s ease",
                                  }}
                                >
                                  Form 73
                                </Button>
                                )}
                                {/* Generate AAFee Button */}
                                {showAAFeeButton == true && (
                                  <Button
                                    onClick={handleGenerateAAFeeClick}
                                    variant="contained"
                                    disabled={
                                      actionLoading || loadingDisciplines
                                    }
                                    size="small"
                                    sx={{
                                      background: `linear-gradient(135deg, ${colors.success.main}, ${colors.success.dark})`,
                                      color: "white",
                                      px: 2,
                                      py: 0.5,
                                      borderRadius: 3,
                                      fontWeight: 500,
                                      fontSize: "0.7rem",
                                      textTransform: "none",
                                      minWidth: "140px",
                                      boxShadow: `0 4px 16px ${alpha(
                                        colors.success.main,
                                        0.3,
                                      )}`,
                                      "&:hover": {
                                        boxShadow: `0 8px 24px ${alpha(
                                          colors.success.main,
                                          0.4,
                                        )}`,
                                        transform: "translateY(-2px)",
                                        background: `linear-gradient(135deg, ${colors.success.dark}, ${colors.success.main})`,
                                      },
                                      transition: "all 0.3s ease",
                                    }}
                                  >
                                    {loadingDisciplines
                                      ? "Loading..."
                                      : "Generate AAFe"}
                                  </Button>
                                )}
                              </>
                            )}

                          {(getCapacityLabel(assessor) === "LA" ||
                            getCapacityLabel(assessor) === "LA_TA") &&
                            (stage === "6b" ||
                              stage === "6d" ||
                              stage === "6e" ||
                              stage === "6f" ||
                              stage === "7" ||
                              stage === "7a" ||
                              stage === "7b" ||
                              stage === "7c" ||
                              stage === "7d" ||
                              stage === "7e" ||
                              stage === "7f" ||
                              stage === "7g" ||
                              stage === "7h" ||
                              stage === "11b" ||
                              stage === "12" ||
                              stage === "12a" ||
                              stage === "12b" ||
                              stage === "13" ||
                              stage === "13a" ||
                              stage === "13b" ||
                              stage === "13c" ||
                              stage === "13d" ||
                              stage === "13e" ||
                              stage === "11f" ||
                              stage === "11g" ||
                              stage === "13") && (
                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => {
                                  const url = `/main/do/ac-process?appId=${encryptAppId(
                                    appId,
                                  )}&orgId=${encryptText(orgId)}&assessmentId=${encryptText(
                                    assessor.AssessorAllotmentId,
                                  )}`;
                                  // navigate(url);
                                  window.open(url, "_blank");
                                }}
                                sx={{
                                  background: `linear-gradient(135deg, ${colors.secondary.main}, ${colors.secondary.dark})`,
                                  color: "white",
                                  px: 2,
                                  py: 0.5,
                                  borderRadius: 3,
                                  fontWeight: 500,
                                  fontSize: "0.7rem",
                                  textTransform: "none",
                                  minWidth: "140px",
                                  boxShadow: `0 4px 16px ${alpha(
                                    colors.secondary.main,
                                    0.3,
                                  )}`,
                                  "&:hover": {
                                    boxShadow: `0 8px 24px ${alpha(
                                      colors.secondary.main,
                                      0.4,
                                    )}`,
                                    transform: "translateY(-2px)",
                                  },
                                  transition: "all 0.3s ease",
                                }}
                              >
                                Decision Process
                              </Button>
                            )}
                          {(getCapacityLabel(assessor) === "LA" ||
                            getCapacityLabel(assessor) === "LA_TA") &&
                            ptPlanUploaded && (
                              <Button
                                variant="contained"
                                size="small"
                                onClick={handleViewPTPlan}
                                sx={{
                                  background: `linear-gradient(135deg, ${colors.primary.main}, ${colors.secondary.main})`,
                                  color: "white",
                                  px: 2,
                                  py: 0.5,
                                  borderRadius: 3,
                                  fontWeight: 500,
                                  fontSize: "0.7rem",
                                  textTransform: "none",
                                  minWidth: "140px",
                                  boxShadow: `0 4px 16px ${alpha(
                                    colors.primary.main,
                                    0.3,
                                  )}`,
                                  "&:hover": {
                                    boxShadow: `0 8px 24px ${alpha(
                                      colors.primary.main,
                                      0.4,
                                    )}`,
                                    transform: "translateY(-2px)",
                                  },
                                  transition: "all 0.3s ease",
                                }}
                              >
                                View PT Plan
                              </Button>
                            )}
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                            alignItems: "flex-start",
                          }}
                        >
                          {(getCapacityLabel(assessor) === "LA" ||
                            getCapacityLabel(assessor) === "LA_TA") &&
                            (stage === "21" ||
                              stage === "6c" ||
                              stage === "6b" ||
                              stage === "6d" ||
                              stage === "6e" ||
                              stage === "6f" ||
                              stage === "7" ||
                              stage === "7a" ||
                              stage === "7b" ||
                              stage === "7c" ||
                              stage === "7d" ||
                              stage === "7e" ||
                              stage === "7f" ||
                              stage === "7g" ||
                              stage === "7h" ||
                              stage === "11b" ||
                              stage === "12" ||
                              stage === "12a" ||
                              stage === "12b" ||
                              stage === "13" ||
                              stage === "13a" ||
                              stage === "13b" ||
                              stage === "13c" ||
                              stage === "13d" ||
                              stage === "13e" ||
                              stage === "11f" ||
                              stage === "11g" ||
                              stage === "13" ||
                              stage === "6a") && (
                              <>
                                <Button
                                  variant="contained"
                                  size="small"
                                  onClick={() => {
                                    const url = `/main/form72?appId=${encodeURIComponent(
                                      encryptAppId(appId),
                                    )}&orgId=${encodeURIComponent(
                                      encryptText(orgId),
                                    )}&assessmentId=${encodeURIComponent(
                                      encryptText(assessor.AssessorAllotmentId),
                                    )}&assessorId=${encodeURIComponent(
                                      encryptText(assessorId),
                                    )}`;

                                    window.open(
                                      url,
                                      "_blank",
                                      "noopener,noreferrer",
                                    );
                                  }}
                                  sx={{
                                    background: `linear-gradient(135deg, ${colors.secondary.main}, ${colors.secondary.dark})`,
                                    color: "white",
                                    px: 2,
                                    py: 0.5,
                                    borderRadius: 3,
                                    fontWeight: 500,
                                    fontSize: "0.7rem",
                                    textTransform: "none",
                                    minWidth: "140px",
                                    boxShadow: `0 4px 16px ${alpha(
                                      colors.secondary.main,
                                      0.3,
                                    )}`,
                                    "&:hover": {
                                      boxShadow: `0 8px 24px ${alpha(
                                        colors.secondary.main,
                                        0.4,
                                      )}`,
                                      transform: "translateY(-2px)",
                                    },
                                    transition: "all 0.3s ease",
                                  }}
                                >
                                  Form 72
                                </Button>
                              </>
                            )}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            mb: 0.5,
                          }}
                        >
                          <PersonIcon
                            fontSize="small"
                            sx={{ mr: 0.5, color: "#1976d2" }}
                          />
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            sx={{ color: "#1976d2" }}
                          >
                            {assessor.AssessorName || "N/A"}
                          </Typography>
                        </Box>
                        {assessor.Email && (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: 0.3,
                            }}
                          >
                            <EmailIcon
                              fontSize="small"
                              sx={{
                                mr: 0.5,
                                color: "#666",
                                fontSize: 14,
                              }}
                            />
                            <Typography variant="caption" color="textSecondary">
                              {assessor.Email}
                            </Typography>
                          </Box>
                        )}
                        {assessor.Mobile && (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <PhoneIcon
                              fontSize="small"
                              sx={{
                                mr: 0.5,
                                color: "#666",
                                fontSize: 14,
                              }}
                            />
                            <Typography variant="caption" color="textSecondary">
                              {assessor.Mobile}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Box sx={{ mb: 1 }}>
                          {type !== "LAB" && (
                            <Typography
                              variant="body2"
                              fontWeight={500}
                              sx={{ mb: 0.5 }}
                            >
                              {assessor.ncCount === 0 ? '' : assessor.descripttion ? assessor.descripttion : 'N/A'}
                            </Typography>
                          )}

                          <Chip
                            label={getCapacityLabel(assessor)}
                            size="small"
                            color="info"
                            variant="outlined"
                            sx={{ fontWeight: 600 }}
                          />
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {labTypeId === 1 || labTypeId === 2 ? (
                        <>
                          <div style={{ color: "#1976d2", fontWeight: 500 }}>
                            {assessor.disciplineName || "N/A"}
                          </div>

                          <div style={{ color: "#2e7d32", fontWeight: 600 }}>
                            {assessor.groupName || ""}
                          </div>
                        </>
                      ) : (
                        <Typography variant="body2" fontWeight={500}>
                          {assessor.disciplineName ||
                            assessor.DisciplineCode ||
                            ""}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          bgcolor: "#e3f2fd",
                          p: 1,
                          borderRadius: 1,
                          border: "1px solid #90caf9",
                        }}
                      >
                        <Typography
                          variant="caption"
                          fontWeight={600}
                          color="primary"
                          display="block"
                        >
                          From:{" "}
                          {formatDate(
                            assessor.DateFrom || assessor.AssessmentFromDate,
                          )}
                        </Typography>
                        <Divider sx={{ my: 0.5 }} />
                        <Typography
                          variant="caption"
                          fontWeight={600}
                          color="primary"
                          display="block"
                        >
                          To:{" "}
                          {formatDate(
                            assessor.DateTo || assessor.AssessmentToDate,
                          )}
                        </Typography>
                      </Box>
                    </TableCell>
                    {type !== "LAB" && (
                      <>
                        <TableCell>
                          <Box>
                            {getStatusChip(assessorAccepted, "Assessor")}
                            {assessorAccepted === 1 &&
                              assessor.AssessorAcceptDate && (
                                <>
                                  <Typography
                                    variant="caption"
                                    display="block"
                                    color="success"
                                    sx={{ mt: 0.5 }}
                                  >
                                    Accepted on:{" "}
                                    {formatDate(assessor.AssessorAcceptDate)}
                                  </Typography>
                                  <Divider sx={{ my: 0.5 }} />
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() =>
                                      handleViewDeclaration(assessor)
                                    }
                                  >
                                    <VisibilityIcon fontSize="small" />
                                  </IconButton>
                                </>
                              )}
                            {assessorAccepted === 2 &&
                              assessor.AssessorDenyReason && (
                                <Box
                                  sx={{
                                    mt: 1,
                                    p: 1,
                                    bgcolor: "#ffebee",
                                    borderRadius: 1,
                                    borderLeft: "3px solid #f44336",
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    display="block"
                                    color="error"
                                    fontWeight={500}
                                  >
                                    Reason:
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    display="block"
                                    color="error"
                                  >
                                    {assessor.AssessorDenyReason}
                                  </Typography>
                                </Box>
                              )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            {getStatusChip(cabAccepted, "CAB")}
                            {cabAccepted === 1 && assessor.CabAcceptDate && (
                              <Typography
                                variant="caption"
                                display="block"
                                color="success"
                                sx={{ mt: 0.5 }}
                              >
                                Accepted on:{" "}
                                {formatDate(assessor.CabAcceptDate)}
                              </Typography>
                            )}
                            {cabAccepted === 2 && assessor.CABDenyReason && (
                              <Box
                                sx={{
                                  mt: 1,
                                  p: 1,
                                  bgcolor: "#ffebee",
                                  borderRadius: 1,
                                  borderLeft: "3px solid #f44336",
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  display="block"
                                  color="error"
                                  fontWeight={500}
                                >
                                  Reason:
                                </Typography>
                                <Typography
                                  variant="caption"
                                  display="block"
                                  color="error"
                                >
                                  {assessor.CABDenyReason}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            {getStatusChip(jdAccepted, "JD")}
                            {jdAccepted === 1 &&
                              assessor.JDirectorAcceptDate && (
                                <Typography
                                  variant="caption"
                                  display="block"
                                  color="success"
                                  sx={{ mt: 0.5 }}
                                >
                                  Accepted on:{" "}
                                  {formatDate(assessor.JDirectorAcceptDate)}
                                </Typography>
                              )}
                            {jdAccepted === 2 &&
                              assessor.JDirectorDenyReason && (
                                <Box
                                  sx={{
                                    mt: 1,
                                    p: 1,
                                    bgcolor: "#ffebee",
                                    borderRadius: 1,
                                    borderLeft: "3px solid #f44336",
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    display="block"
                                    color="error"
                                    fontWeight={500}
                                  >
                                    Reason:
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    display="block"
                                    color="error"
                                  >
                                    {assessor.JDirectorDenyReason}
                                  </Typography>
                                </Box>
                              )}
                          </Box>
                        </TableCell>
                      </>
                    )}

                    {type === "JD" && (
                      <TableCell>
                        {(jdAccepted === 0 || jdAccepted === null) && (
                          <Box sx={{ display: "flex", gap: 1 }}>
                            <Tooltip title="Accept Assessor">
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => handleAccept(assessorId)}
                                disabled={actionLoading}
                                sx={{
                                  bgcolor: "#e8f5e9",
                                  "&:hover": {
                                    bgcolor: "#c8e6c9",
                                  },
                                }}
                              >
                                <CheckIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Reject Assessor">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleRejectClick(assessorId)}
                                disabled={actionLoading}
                                sx={{
                                  bgcolor: "#ffebee",
                                  "&:hover": {
                                    bgcolor: "#ffcdd2",
                                  },
                                }}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}


      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Success Display */}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}


      {/* Reject Dialog */}
      <Dialog
        open={rejectDialogOpen}
        onClose={() => !actionLoading && setRejectDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: '#f44336', color: 'white', fontWeight: 600 }}>
          Reject Assessor
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <TextField
            autoFocus
            margin="dense"
            label="Reason for Rejection"
            type="text"
            fullWidth
            multiline
            rows={4}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Please provide a detailed reason for rejecting this assessor"
            disabled={actionLoading}
            required
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setRejectDialogOpen(false)}
            disabled={actionLoading}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleRejectConfirm}
            variant="contained"
            color="error"
            disabled={actionLoading || !rejectReason.trim()}
            startIcon={actionLoading ? <CircularProgress size={16} /> : <CloseIcon />}
          >
            {actionLoading ? 'Rejecting...' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Send to Dealing Officer Confirmation Dialog */}
      <Dialog
        open={sendToDODialogOpen}
        onClose={() => !actionLoading && setSendToDODialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: '#ff9800', color: 'white', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
          <SendIcon />
          Send to Dealing Officer
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to send this application back to the dealing officer?
          </Typography>
          <TextField
            fullWidth
            type="password"
            label="Confirm Password *"
            placeholder="Enter your password to confirm"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={actionLoading}
            error={!confirmPassword && error}
            helperText={!confirmPassword && error ? 'Password is required' : ''}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => {
              setSendToDODialogOpen(false);
              setConfirmPassword('');
            }}
            disabled={actionLoading}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={confirmSendToDealingOfficer}
            variant="contained"
            color="warning"
            disabled={actionLoading || !confirmPassword.trim()}
            startIcon={actionLoading ? <CircularProgress size={16} /> : <SendIcon />}
          >
            {actionLoading ? 'Sending...' : 'Send to DO'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Finalize Assessment Confirmation Dialog */}
      <Dialog
        open={finalizeDialogOpen}
        onClose={() => !actionLoading && setFinalizeDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: '#4caf50', color: 'white', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckIcon />
          Schedule Final Assessment
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to finalize this assessment?
          </Typography>
          <TextField
            fullWidth
            type="password"
            label="Confirm Password *"
            placeholder="Enter your password to confirm"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={actionLoading}
            error={!confirmPassword && error}
            helperText={!confirmPassword && error ? 'Password is required' : ''}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => {
              setFinalizeDialogOpen(false);
              setConfirmPassword('');
            }}
            disabled={actionLoading}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={confirmFinalizeAssessment}
            variant="contained"
            color="success"
            disabled={actionLoading || !confirmPassword.trim()}
            startIcon={actionLoading ? <CircularProgress size={16} /> : <CheckIcon />}
          >
            {actionLoading ? 'Finalizing...' : 'Finalize Assessment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Generate AAFee Confirmation Dialog */}
      <Dialog
        open={generateAAFeeConfirmOpen}
        onClose={() => setGenerateAAFeeConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: '#2196f3', color: 'white', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
          Generate AAFe
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
            Are you sure you want to generate AAFe?
          </Typography>
          <Typography variant="body1" color="textSecondary">
            This action will initiate the AAFe generation process for this application.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setGenerateAAFeeConfirmOpen(false)}
            variant="outlined"
          >
            No, Cancel
          </Button>
          <Button
            onClick={handleGenerateAAFeeConfirm}
            variant="contained"
            color="primary"
            disabled={loadingDisciplines}
          >
            {loadingDisciplines ? 'Loading...' : 'Yes, Continue'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Generate AAFee - Discipline Selection Dialog */}
      <Dialog
        open={generateAAFeeDialogOpen}
        onClose={() => !actionLoading && setGenerateAAFeeDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: '#2196f3', color: 'white', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
          Generate AAFe - Select Disciplines
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {/* CAB Details Information */}
          <>
            {labType === 'Medical' && (
              <>
                <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                  <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', fontWeight: 'bold' }}>
                    Laboratory Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                        Lab Type:
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {labType || 'Medical'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 1 }}>
                        Collection Centers:
                      </Typography>
                      <TextField
                        type="number"
                        value={editableCollectionCenters}
                        onChange={handleCollectionCenterChange}
                        size="small"
                        disabled={aaFeeGenerated}
                        inputProps={{ min: 0, step: 1 }}
                        sx={{
                          width: '100%',
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: aaFeeGenerated ? '#f5f5f5' : 'white'
                          }
                        }}
                        helperText={aaFeeGenerated ? "Cannot edit after generation" : "Enter number of collection centers"}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                        Accreditation Category:
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {accreditationCategory || 'N/A'}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Please review the laboratory information above and modify the number of collection centers if needed. Then select the disciplines for which you want to generate AAFe:
                </Typography>

                {!aaFeeGenerated && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    💡 You can edit the number of collection centers above to adjust the lab category classification.
                  </Alert>
                )}
              </>
            )}

            {(labType !== 'Medical' || CabType === 'Medical Imaging') && (
              <>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Please select the disciplines for which you want to generate AAFe:
                </Typography>

                {aaFeeGenerated && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    AAFe has already been generated for this application. All disciplines are selected and cannot be modified.
                  </Alert>
                )}

                <Box sx={{ maxHeight: '300px', overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1, p: 2 }}>
                  {disciplineListAAFee.map((discipline) => (
                    <Box
                      key={discipline.DisciplineId}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        py: 1,
                        px: 1,
                        borderRadius: 1,
                        '&:hover': {
                          bgcolor: '#f5f5f5'
                        }
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={() => handleDisciplineChange(discipline.DisciplineId, !selectedDisciplines.includes(discipline.DisciplineId))}
                        disabled={aaFeeGenerated}
                        sx={{ mr: 1 }}
                      >
                        {selectedDisciplines.includes(discipline.DisciplineId) ? (
                          <CheckBoxIcon color="primary" />
                        ) : (
                          <CheckBoxOutlineBlankIcon />
                        )}
                      </IconButton>
                      <Typography
                        variant="body1"
                        sx={{
                          flexGrow: 1,
                          cursor: aaFeeGenerated ? 'default' : 'pointer',
                          opacity: aaFeeGenerated ? 0.7 : 1
                        }}
                        onClick={() => !aaFeeGenerated && handleDisciplineChange(discipline.DisciplineId, !selectedDisciplines.includes(discipline.DisciplineId))}
                      >
                        {discipline.disciplineName}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                {selectedDisciplines.length === 0 && !aaFeeGenerated && (
                  <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                    Please select at least one discipline to continue.
                  </Typography>
                )}
              </>
            )}
          </>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => {
              setGenerateAAFeeDialogOpen(false);
              setSelectedDisciplines([]);
            }}
            disabled={actionLoading}
            variant="outlined"
          >
            Cancel
          </Button>
          {!aaFeeGenerated && (
            <Button
              onClick={handleAAFeeSubmit}
              variant="contained"
              color="primary"
              disabled={
                labType === "Medical"
                  ? actionLoading
                  : actionLoading || selectedDisciplines.length === 0
              }
              startIcon={actionLoading ? <CircularProgress size={16} /> : null}
            >
              {actionLoading ? 'Generating...' : 'Generate AAFe'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AssessorDetailsView;
